// Description: Page to add a new integration
// Updated: 24/06/2025
// Author: Simon Franklin

// React
import React from "react";

// Material UI Components
import { Box, Button, Typography } from "@mui/material";

// Components and functions for the project
import HeaderPage from "../../components/HeaderPage/HeaderPage";
import use123zapStore from "../../context/123zapStore";
import { notify } from "../../utils/utils";
import { useNavigate } from "react-router-dom";

export const AlertIcon = () => {
  return (
    <svg
      width="18"
      height="17"
      viewBox="0 0 18 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.25" cy="8.5" r="8" stroke="#7C87AA" />
      <path
        d="M8.79364 12.9187V6.292H9.66164V12.9187H8.79364ZM9.23231 5.592C9.07675 5.592 8.94609 5.53911 8.84031 5.43333C8.73453 5.32756 8.68164 5.2 8.68164 5.05067C8.68164 4.90133 8.73453 4.77378 8.84031 4.668C8.94609 4.556 9.07675 4.5 9.23231 4.5C9.38164 4.5 9.5092 4.556 9.61497 4.668C9.72075 4.77378 9.77364 4.90133 9.77364 5.05067C9.77364 5.2 9.72075 5.32756 9.61497 5.43333C9.5092 5.53911 9.38164 5.592 9.23231 5.592Z"
        fill="#7C87AA"
      />
    </svg>
  );
};

const AddIntegration = () => {
  const id = localStorage.getItem("integration_id") || null;
  const integrations = use123zapStore((state) => state.integrations);
  const setAlreadyFetchIntegrations = use123zapStore((state) => state.setAlreadyFetchIntegrations);
  const selectedIntegration = integrations.find((item) => item.id === id);
  const [disableButton, setDisableButton] = React.useState(false);
  const navigate = useNavigate();

  const formData = {
    "Controllr ISP": ["url", "username", "password"],
    HubSoft: ["url", "client_id", "client_secret", "username", "password"],
    SGP: ["url", "app", "token"],
    EliteSoft: ["url", "_user", "_passwd"],
    Sienge: ["url", "username", "password"]
  };

  const initializeFormState = (fields) => {
    return fields.reduce((acc, field) => {
      acc[field] = "";
      return acc;
    }, {});
  };

  const [formState, setFormState] = React.useState({});

  React.useEffect(() => {
    if (selectedIntegration) {
      setFormState(initializeFormState(formData[selectedIntegration.title]));
    }
  }, [selectedIntegration]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setDisableButton(true);

    const finalUrl = (integration) => {
      if (integration === "SGP") {
        return "sgp";
      } else if (integration === "Controllr ISP") {
        return "controllr";
      } else if (integration === "HubSoft") {
        return "hubsoft";
      } else if (integration === "EliteSoft") {
        return "elitesoft";
      } else if (integration === "Sienge") {
        return "sienge";
      }
    };

    try {
      const token = localStorage.getItem("jwt_token");
      const clientId = localStorage.getItem("idToken");

      if (!token) throw new Error("Token de autenticação não encontrado");
      if (!clientId) throw new Error("ID do cliente não encontrado");

      await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/${finalUrl(
          selectedIntegration.title
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            Client_ID: `${clientId}`,
          },
          body: JSON.stringify(formState),
        }
      )
        .then((response) => response.json())
        .then((response) => {
          if ((response.message || response.status) === "success") {
            notify("Success", "Integração adicionada com sucesso", true);
            setAlreadyFetchIntegrations(false);
            navigate("/integrações");
          }
        });
    } catch (error) {
      console.error(error);
      throw new Error("Erro ao buscar dados da API");
    } finally {
      setDisableButton(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <HeaderPage />
      <Box
        component="main"
        sx={{
          display: "flex",
          padding: "32px",
          gap: { xs: "24px", sm: "64px" },
          justifyContent: { xs: "normal", sm: "space-between" },
          minHeight: "calc(100vh - 100px)",
          minWidth: "100%",
          flexDirection: { xs: "column", sm: "row" }
        }}
      >
        <Box
          component="section"
          sx={{
            width: { xs: "100%", sm: "calc(100% - 32px)" }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "auto",
              borderRadius: "4px",
              border: "1px solid #E0E0E0",
              padding: "24px",
              backgroundColor: "#fff",
              gap: "24px",
            }}
          >
            <Box
              component="img"
              src={selectedIntegration.logo}
              alt={`logo ${selectedIntegration.title}`}
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                backgroundColor: "#bdbdbd",
                minWidth: "32px",
                minHeight: "32px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "16px" },
                }}
              >
                {selectedIntegration.title}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: "12px", sm: "16px" },
                }}
              >
                {selectedIntegration.description}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          component="section"
          sx={{
            width: { xs: "100%", sm: "calc(100% - 32px)" }
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              border: "1px solid #d9d9dc",
              height: "100%",
              backgroundColor: "#fff",
              padding: { xs: "24px", sm: "48px" },
            }}
          >
            {Object.keys(formState).map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginBottom: "32px",
                }}
              >
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      textTransform: "uppercase",
                      fontSize: "17px",
                      fontWeight: 600,
                    }}
                  >
                    {item}
                  </Typography>
                  <AlertIcon />
                </Box>

                <input
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      [item]: e.target.value,
                    }))
                  }
                  type={item === "password" ? "password" : "text"}
                  name={item}
                  placeholder={item}
                  value={formState[item]}
                  style={{
                    height: "48px",
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    backgroundColor: "#F8F9FB",
                    padding: "17px 24px",
                    outline: "none"
                  }}
                />
              </Box>
            ))}

            <Button
              variant="contained"
              disabled={disableButton}
              type="submit"
              sx={{
                height: "54px",
                width: "100%",
                borderRadius: "6px",
                fontSize: "18px",
                fontWeight: 500,
                marginTop: "2px",
              }}
            >
              Conectar
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddIntegration;